import React, {
    useState,
    useMemo,
    useCallback,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import SliderPattern from '@mlb/react-patterns/components/Slider/slider';
import { Rail } from '@mlb/react-patterns/components/Slider/slider.style';
import Btn from '@mlb/react-patterns/components/Button/button.tsx';
import COLORS from '@mlb/react-patterns/style/universal.color';

import { useDidUpdate } from 'bdvp-common/src/hooks';

const Wrapper = styled.div`
    padding: 0px 0px 30px 0px;
`;

const Label = styled.p`
    font-size: 17px;
    color: ${({ disabled }) => disabled ? COLORS.mlbGrey4 : COLORS.mlbWhite };
    margin: 0;
`;

const LabelWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 30px;
`;

const Button = styled(Btn)`
    margin: -4px -10px 0 0;
`;

const Slider = styled(SliderPattern)`
    width: calc(100% - 20px);
    margin: 0 10px;

    ${Rail} {
        width: calc(100% + 20px);
        margin: 0 -10px;
    }
`;

export const formatValue = (value = 0, shouldRoundUp, step = 1) => {

    if(isNaN(value)) {

        return 0;

    }

    const round = shouldRoundUp ? Math.ceil : Math.floor;
    return  step * round(value / step);

};

const RangeSlider = (props) => {

    const {
        fields,
        onChange,
        componentConfig,
    } = props;
    const {
        selected,
        range,
        unitOfMeasurement,
        ...field
    } = fields[0];
    const unitOfMeasurementValue = unitOfMeasurement?.value ?? '';

    const step = componentConfig?.sliderStep ?? 1;
    const minValue = useMemo(() => formatValue(range?.[0]?.min, false, step), [range?.[0]?.min]);
    const maxValue = useMemo(() => formatValue(range?.[0]?.max, true, step), [range?.[0]?.max]);
    const selectedValue = selected[0]?.value || `{{ ${minValue}, ${maxValue} }}`;
    const [
        fromValue,
        toValue,
    ] = useMemo(() => {

        return selectedValue.replace(/{|}|"| /g, '').split(',')
            .map((val, index) => formatValue(val, index !== 0, step));

    }, [
        selectedValue,
        step,
    ]);

    const [
        value,
        setValue,
    ] = useState([
        fromValue,
        toValue,
    ]);

    useDidUpdate(() => {

        setValue([
            fromValue,
            toValue,
        ]);

    }, [
        fromValue,
        toValue,
    ]);

    const clearFilter = useCallback(() => {

        const data = {
            changes: {
                value: `{{ ${fromValue}, ${toValue} }}`,
                label: `${fromValue} ${unitOfMeasurementValue} - ${toValue} ${unitOfMeasurementValue}`,
            },
            event: 'deselect',
            selected: [],
        };

        onChange(data, field);

    }, [
        unitOfMeasurementValue,
        fromValue,
        toValue,
        onChange,
        field,
    ]);

    const onChangeHandler = useCallback((value) => {

        if (minValue === value[0] && maxValue === value[1]) {

            return clearFilter();

        }

        const changes = {
            value: `{{ ${value[0]}, ${value[1]} }}`,
            label: `${value[0]} ${unitOfMeasurementValue} - ${value[1]} ${unitOfMeasurementValue}`,
        };
        const data = {
            changes: changes,
            selected: [changes],
            event: 'select',
        };

        onChange(data, field);

    }, [
        clearFilter,
        onChange,
        minValue,
        maxValue,
        field,
    ]);

    const onUpdateHandler = useCallback((value) => setValue(value), [setValue]);

    const sliderHandlers = useMemo(() => {

        return {
            onChange: onChangeHandler,
            onUpdate: onUpdateHandler,
        };

    }, [
        onChangeHandler,
        onUpdateHandler,
    ]);

    const hasChanged = !(minValue === value[0] && maxValue === value[1]);

    return (
        <Wrapper>
            <LabelWrapper>
                <Label
                    disabled={!hasChanged}
                >
                    {`${value[0]} - ${value[1]} ${unitOfMeasurementValue}`}
                </Label>
                {hasChanged && (
                    <Button
                        iconName={'close'}
                        handlers={{ onClick: clearFilter }}
                        styleName={'default'}
                        shape={'hollow-borderless'}
                        hasHover={false}
                    />
                )}
            </LabelWrapper>
            <Slider
                defaultValues={value}
                maxValue={maxValue}
                minValue={minValue}
                handlers={sliderHandlers}
                step={step}
            />
        </Wrapper>
    );

};

RangeSlider.propTypes = {
    onChange: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            selected: PropTypes.array.isRequired,
        }).isRequired
    ).isRequired,
};

export default RangeSlider;
